const REFERRAL_QUERY_KEYS = [
  'ref',
  'realtorId',
  'realtor_id',
  'sourceRealtorId',
  'source_realtor_id',
  'referralId',
  'referral_id',
];

const INVALID_REFERRAL_IDS = new Set(['null', 'undefined', 'none', 'direct']);
const FIRST_REFERRAL_AUDIT_QUERY_KEYS = {
  id: 'first_referrer_id',
  capturedAt: 'first_referrer_at',
  captureSource: 'first_referrer_capture',
};
const FIRST_REFERRAL_CAPTURE_SOURCES = new Set([
  'query',
  'active-cookie-bootstrap',
  'legacy-cookie-bootstrap',
]);
const REFERRAL_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

export const normalizeReferralId = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized || INVALID_REFERRAL_IDS.has(normalized.toLowerCase())) return '';
  return /^[A-Za-z0-9_-]{3,128}$/.test(normalized) ? normalized : '';
};

const parseSourceUrl = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  try {
    return new URL(normalized, 'https://ieum.invalid');
  } catch {
    return null;
  }
};

const parseTimeMs = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (Number.isFinite(value?.seconds)) return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const referralIdFromSourceUrl = (sourceUrl) => {
  const parsed = parseSourceUrl(sourceUrl);
  if (!parsed) return '';
  for (const key of REFERRAL_QUERY_KEYS) {
    const referralId = normalizeReferralId(parsed.searchParams.get(key));
    if (referralId) return referralId;
  }
  return '';
};

const isLandingSource = (source, sourceType, sourceUrl) => {
  const sourceValues = [source, sourceType]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);
  if (sourceValues.some((value) => value === 'landing' || value === 'ieum-landing')) return true;

  const parsed = parseSourceUrl(sourceUrl);
  return parsed?.hostname === 'ieum-landing.web.app' && parsed.pathname.startsWith('/apply');
};

export const resolveReferralAttribution = ({
  explicitRealtorId,
  sourceUrl,
  source,
  sourceType,
} = {}) => {
  const explicitId = normalizeReferralId(explicitRealtorId);
  const sourceUrlId = referralIdFromSourceUrl(sourceUrl);
  const sourceRealtorId = explicitId || sourceUrlId;

  if (sourceRealtorId) {
    return {
      kind: 'realtor',
      sourceRealtorId,
      recoveredFromSourceUrl: !explicitId && Boolean(sourceUrlId),
    };
  }
  if (isLandingSource(source, sourceType, sourceUrl)) {
    return { kind: 'direct_landing', sourceRealtorId: '', recoveredFromSourceUrl: false };
  }
  if ([source, sourceType].some((value) => String(value || '').trim().toLowerCase() === 'direct')) {
    return { kind: 'direct', sourceRealtorId: '', recoveredFromSourceUrl: false };
  }
  return { kind: 'unknown', sourceRealtorId: '', recoveredFromSourceUrl: false };
};

export const firstReferralAuditFromSourceUrl = (sourceUrl) => {
  const parsed = parseSourceUrl(sourceUrl);
  if (!parsed) return null;
  const rawId = parsed.searchParams.get(FIRST_REFERRAL_AUDIT_QUERY_KEYS.id);
  const rawCapturedAt = parsed.searchParams.get(FIRST_REFERRAL_AUDIT_QUERY_KEYS.capturedAt);
  const rawCaptureSource = parsed.searchParams.get(FIRST_REFERRAL_AUDIT_QUERY_KEYS.captureSource);
  if (rawId === null && rawCapturedAt === null && rawCaptureSource === null) return null;

  const firstReferralId = normalizeReferralId(rawId);
  const capturedAtMs = parseTimeMs(rawCapturedAt);
  const captureSource = String(rawCaptureSource || '').trim();
  const valid = Boolean(
    firstReferralId
    && capturedAtMs
    && FIRST_REFERRAL_CAPTURE_SOURCES.has(captureSource),
  );
  return {
    firstReferralId,
    capturedAt: capturedAtMs ? new Date(capturedAtMs).toISOString() : '',
    capturedAtMs,
    captureSource,
    valid,
  };
};

export const resolveFirstReferralAudit = ({
  sourceUrl,
  activeRealtorId,
  requestCreatedAt,
} = {}) => {
  const audit = firstReferralAuditFromSourceUrl(sourceUrl);
  if (!audit) {
    return {
      kind: 'none',
      firstReferralId: '',
      capturedAt: '',
      captureSource: '',
      elapsedMs: null,
    };
  }
  if (!audit.valid) {
    return {
      kind: 'invalid',
      ...audit,
      elapsedMs: null,
    };
  }

  const normalizedActiveRealtorId = normalizeReferralId(activeRealtorId);
  const requestCreatedAtMs = parseTimeMs(requestCreatedAt);
  const elapsedMs = requestCreatedAtMs
    ? requestCreatedAtMs - audit.capturedAtMs
    : null;
  if (normalizedActiveRealtorId) {
    return {
      kind: 'active',
      ...audit,
      activeRealtorId: normalizedActiveRealtorId,
      elapsedMs,
    };
  }
  if (elapsedMs === null) {
    return { kind: 'request_time_unknown', ...audit, elapsedMs };
  }
  if (elapsedMs < 0) {
    return { kind: 'invalid_timestamp', ...audit, elapsedMs };
  }
  if (elapsedMs >= REFERRAL_RETENTION_MS) {
    return { kind: 'expired', ...audit, elapsedMs };
  }
  return { kind: 'missing_within_retention', ...audit, elapsedMs };
};

export const buildFirstReferralAuditStatusLabel = (audit) => {
  switch (audit?.kind) {
    case 'active': return '추천 적용 중';
    case 'expired': return '7일 만료 후 직접 접수';
    case 'missing_within_retention': return '7일 이내 추천값 유실 의심';
    case 'request_time_unknown': return '접수시각 확인 필요';
    case 'invalid_timestamp': return '유입·접수시각 역전 확인 필요';
    case 'invalid': return '감사 기록 불완전';
    default: return '';
  }
};

export const buildFirstReferralCaptureSourceLabel = (captureSource) => {
  switch (captureSource) {
    case 'query': return '추천 링크 최초 방문';
    case 'active-cookie-bootstrap': return '기존 활성 추천값 최초 기록';
    case 'legacy-cookie-bootstrap': return '과거 추천 쿠키 이관';
    default: return '';
  }
};

export const buildAttributionLabel = (attribution, realtorName = '') => {
  if (attribution?.kind === 'realtor') {
    return realtorName && realtorName !== '-' ? realtorName : '추천부동산 (계정 확인 필요)';
  }
  if (attribution?.kind === 'direct_landing') return '직접 유입 (이음 랜딩)';
  if (attribution?.kind === 'direct') return '직접 유입';
  return '유입경로 확인 필요';
};

export const formatSourceUrlForAdmin = (sourceUrl) => {
  const parsed = parseSourceUrl(sourceUrl);
  if (!parsed) return '';
  const host = parsed.hostname === 'ieum.invalid' ? '' : parsed.hostname;
  const referralMarker = referralIdFromSourceUrl(sourceUrl) ? '?ref=확인됨' : '';
  return `${host}${parsed.pathname}${referralMarker}` || '';
};
